/**
 * SynthAmbientPanel - Synthesized ambient sound controls
 * Uses useSynthAmbient hook for state management
 */
import React, { memo } from 'react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const SynthAmbientPanel = memo(({ 
  isEnabled,
  currentType,
  volume,
  presets,
  onToggle,
  onTypeChange,
  onVolumeChange,
  disabled = false
}) => {
  // Group presets by category — data-driven so new categories added to
  // SYNTH_PRESETS surface automatically without another render branch.
  const CATEGORY_ORDER = ['Pads', 'Nature', 'Tonal', 'Noise'];
  const CATEGORY_META = {
    Pads:   { label: '🎹 Pads & Drones',      headerClass: 'text-indigo-300 bg-indigo-500/10' },
    Nature: { label: '🌿 Nature',              headerClass: 'text-green-300 bg-green-500/10' },
    Tonal:  { label: '🔔 Tonal',               headerClass: 'text-amber-300 bg-amber-500/10' },
    Noise:  { label: '📻 Noise (focus & sleep)', headerClass: 'text-cyan-300 bg-cyan-500/10' },
  };
  const categories = Object.fromEntries(
    CATEGORY_ORDER.map(cat => [
      cat,
      Object.entries(presets).filter(([, p]) => p.category === cat),
    ])
  );

  return (
    <div className="space-y-3 p-3 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-lg border border-indigo-400/30">
      {/* Header */}
      <div className="flex items-center justify-between">
        <label className="text-white text-sm font-medium flex items-center gap-2">
          🎹 Synth Ambient
          <span className="text-xs text-indigo-300">(Built-in)</span>
        </label>
        <Button
          onClick={onToggle}
          disabled={disabled}
          size="sm"
          className={`h-7 px-3 ${isEnabled 
            ? 'bg-red-600 hover:bg-red-700' 
            : 'bg-indigo-600 hover:bg-indigo-700'}`}
        >
          {isEnabled ? '⏹ Stop' : '▶ Play'}
        </Button>
      </div>
      
      {/* Description */}
      <div className="text-white/60 text-xs">
        Synthesized ambient sounds • No upload needed
      </div>
      
      {/* Sound Type Selector */}
      <div className="space-y-2">
        <label className="text-white text-xs font-medium">Sound Type</label>
        <Select value={currentType} onValueChange={onTypeChange}>
          <SelectTrigger className="bg-white/10 border-white/20 text-white text-sm h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-white/20 max-h-64">
            {CATEGORY_ORDER.map((cat, ci) => {
              const rows = categories[cat];
              if (!rows || rows.length === 0) return null;
              const meta = CATEGORY_META[cat];
              return (
                <React.Fragment key={cat}>
                  <div className={`px-2 py-1 text-xs font-semibold ${meta.headerClass} ${ci > 0 ? 'mt-1' : ''}`}>
                    {meta.label}
                  </div>
                  {rows.map(([key, preset]) => (
                    <SelectItem key={key} value={key} className="text-white hover:bg-white/10">
                      <div className="flex flex-col">
                        <span>{preset.name}</span>
                        <span className="text-xs text-white/50">{preset.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </React.Fragment>
              );
            })}
          </SelectContent>
        </Select>
      </div>
      
      {/* Volume Control */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-white text-xs font-medium">Volume</label>
          <span className="text-indigo-300 text-xs">{Math.round(volume[0] * 100)}%</span>
        </div>
        <Slider
          value={volume}
          onValueChange={onVolumeChange}
          max={1}
          min={0}
          step={0.05}
          className="text-indigo-400"
        />
      </div>
      
      {/* Status */}
      {isEnabled && (
        <div className="text-xs p-2 rounded bg-indigo-500/20 text-indigo-200">
          🎵 Playing in high-quality stereo
        </div>
      )}
    </div>
  );
});

SynthAmbientPanel.displayName = 'SynthAmbientPanel';

export default SynthAmbientPanel;
